import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button, Spinner, Alert } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import NumberFormat from "react-number-format";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import NotificationService from "../../../services/NotificationsServices";

const notificationTypes = [
  { value: "whatsapp", label: "Mensagem no WhatsApp" },
  { value: "platform", label: "Mensagem na Plataforma" },
];

// Schema de validação
const schema = yup.object().shape({
  keyword: yup.string().required("Palavra-chave é obrigatória"),
  notificationTypes: yup
    .array()
    .min(1, "Selecione pelo menos um tipo de notificação")
    .required("Tipo de notificação é obrigatório"),
  phone: yup.string().when("notificationTypes", {
    is: (types) => types && types.some((type) => type.value === "whatsapp"),
    then: () =>
      yup
        .string()
        .required("Telefone é obrigatório quando WhatsApp está selecionado")
        .min(10, "Telefone deve ter no mínimo 10 dígitos"),
  }),
});

const NotificationsModal = ({ show, onHide, onSave, initialData = {} }) => {
  const [showPhoneField, setShowPhoneField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      keyword: "",
      notificationTypes: [],
      phone: "",
    },
  });

  // Observa mudanças nos tipos de notificação para mostrar/ocultar campos
  const selectedTypes = watch("notificationTypes");

  useEffect(() => {
    if (
      selectedTypes &&
      selectedTypes.some((type) => type.value === "whatsapp")
    ) {
      setShowPhoneField(true);
    } else {
      setShowPhoneField(false);
    }
  }, [selectedTypes]);

  // Reset o formulário quando o componente recebe novos dados iniciais
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {

      // Certificando-se de que notificationTypes está no formato correto
      const formattedNotificationTypes = initialData.notificationTypes || [];

      reset({
        keyword: initialData.keyword || "",
        notificationTypes: formattedNotificationTypes,
        phone: initialData.phone || "",
      });

      // Verificar se há tipos de notificação do WhatsApp
      if (
        formattedNotificationTypes.some((type) => type.value === "whatsapp")
      ) {
        setShowPhoneField(true);
      } else {
        setShowPhoneField(false);
      }
    }
  }, [initialData, reset]);

  const onSubmit = async (data) => {
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      // Preparar dados para envio
      const payload = {
        ...data,
        id: initialData?.id, // Incluir o ID se existir
      };

      if (onSave) {
        await onSave(payload);
        setSuccess(true);
      }
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
      setError(
        error.response?.data?.message ||
          "Erro ao salvar as configurações. Verifique os dados e tente novamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
          Configurações salvas com sucesso!
        </Alert>
      )}

      <Row className="mb-3">
        <Form.Group as={Col} controlId="keyword">
          <Form.Label>Palavra-chave para notificação</Form.Label>
          <Controller
            name="keyword"
            control={control}
            render={({ field }) => (
              <Form.Control
                {...field}
                placeholder="Ex: atendente, humano, pessoa"
                isInvalid={!!errors.keyword}
              />
            )}
          />
          {errors.keyword && (
            <Form.Control.Feedback type="invalid">
              {errors.keyword.message}
            </Form.Control.Feedback>
          )}
          <Form.Text className="text-muted">
            O sistema enviará notificações quando o cliente usar esta palavra
          </Form.Text>
        </Form.Group>
      </Row>

      <Row className="mb-3">
        <Form.Group as={Col} controlId="notificationTypes">
          <Form.Label>Tipos de notificação</Form.Label>
          <Controller
            name="notificationTypes"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                isMulti
                options={notificationTypes}
                classNamePrefix="select"
                placeholder="Selecione os tipos de notificação"
                className={errors.notificationTypes ? "is-invalid" : ""}
              />
            )}
          />
          {errors.notificationTypes && (
            <div className="invalid-feedback d-block">
              {errors.notificationTypes.message}
            </div>
          )}
        </Form.Group>
      </Row>

      {showPhoneField && (
        <Row className="mb-3">
          <Form.Group as={Col} controlId="phone">
            <Form.Label>Telefone para notificações no WhatsApp</Form.Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <NumberFormat
                  format="(##) #####-####"
                  className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                  placeholder={"Telefone"}
                  onValueChange={(values) => {
                    field.onChange(values.value);
                  }}
                  value={field.value}
                />
              )}
            />
            {errors.phone && (
              <Form.Control.Feedback type="invalid">
                {errors.phone.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Row>
      )}

      <div className="d-flex justify-content-end mt-4">
        {onHide && (
          <Button
            variant="secondary"
            onClick={onHide}
            className="me-2"
            disabled={submitting}
          >
            Cancelar
          </Button>
        )}

        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Salvando...
            </>
          ) : (
            "Salvar"
          )}
        </Button>
      </div>
    </Form>
  );
};

export default NotificationsModal;
